import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    Cascader, Form,
    Input, InputNumber, message,
    Modal,
    notification,
    Progress,
    Select,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {debounce} from 'lodash';
import {
    CaretDownOutlined,
    CaretUpOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined, DeleteOutlined, DoubleRightOutlined, EditOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined, InfoCircleOutlined,
    PlusSquareOutlined,
    ReloadOutlined,
    SearchOutlined,
    SortAscendingOutlined
} from '@ant-design/icons';
import s2025 from './styles/store2025.module.css'
import axios from 'axios'
import {get_cookie} from "../../helpers/get_cookie";
import HistoryTable from "./HistoryTable";
import StoreProgressBars from "./StoreProgressBars";
import AddProductModal from "./AddProductModal";
import TransferModal from "./TransferModal";
import ExcelExportButton from "./ExcelExportButton";
import {instance} from "../../API/API";



const getDate = (date, options = {}) => {
    const {
        showIcon = true,
        showTooltip = true,
        compact = false
    } = options;

    // Обработка случая без данных
    if (!date || date.trim() === '') {
        const content = (
            <Tag
                color="default"
                icon={showIcon ? <ClockCircleOutlined/> : null}
                style={{margin: 0}}
            >
                {compact ? '—' : 'Нет даты'}
            </Tag>
        );

        return showTooltip ? (
            <Tooltip title="Дата не указана">
                {content}
            </Tooltip>
        ) : content;
    }

    // Обработка бессрочного хранения
    if (date.toLowerCase() === 'n' || date.toLowerCase() === 'бессрочно') {
        const content = (
            <Tag
                color="green"
                icon={showIcon ? <CheckCircleOutlined/> : null}
                style={{margin: 0}}
            >
                {compact ? '∞' : 'Бессрочно'}
            </Tag>
        );

        return showTooltip ? (
            <Tooltip title="Бессрочное хранение">
                {content}
            </Tooltip>
        ) : content;
    }

    try {
        // Парсинг различных форматов дат
        let expirationDate;

        if (date.includes('.')) {
            // Формат DD.MM.YYYY
            const [day, month, year] = date.split('.');
            expirationDate = new Date(`${year}-${month}-${day}`);
        } else if (date.includes('-')) {
            // Формат YYYY-MM-DD
            expirationDate = new Date(date);
        } else {
            // Попытка парсинга как timestamp
            expirationDate = new Date(date);
        }

        // Проверка валидности даты
        if (isNaN(expirationDate.getTime())) {
            throw new Error('Invalid date format');
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        expirationDate.setHours(0, 0, 0, 0);

        const isExpired = expirationDate < today;
        const isToday = expirationDate.getTime() === today.getTime();
        const daysDifference = Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24));

        let tagProps = {
            style: {margin: 0},
            icon: showIcon ? (isExpired ? <ExclamationCircleOutlined/> : <CheckCircleOutlined/>) : null
        };

        let tooltipText = '';

        if (isExpired) {
            tagProps.color = 'error';
            tooltipText = `Просрочено ${Math.abs(daysDifference)} дней назад`;
        } else if (isToday) {
            tagProps.color = 'orange';
            tooltipText = 'Истекает сегодня';
        } else if (daysDifference <= 7) {
            tagProps.color = 'orange';
            tooltipText = `Истекает через ${daysDifference} дней`;
        } else {
            tagProps.color = 'success';
            tooltipText = `Истекает через ${daysDifference} дней`;
        }

        const displayDate = compact && !isExpired ?
            `+${daysDifference}d` :
            date;

        const content = (
            <Tag {...tagProps}>
                {displayDate}
            </Tag>
        );

        return showTooltip ? (
            <Tooltip title={tooltipText}>
                {content}
            </Tooltip>
        ) : content;

    } catch (error) {
        const content = (
            <Tag color="default" style={{margin: 0}}>
                {compact ? '!' : `Ошибка: ${date}`}
            </Tag>
        );

        return showTooltip ? (
            <Tooltip title="Неверный формат даты">
                {content}
            </Tooltip>
        ) : content;
    }
};
const ItemName = ({name, category, brand, description, date_avalible}) => {
    return (
        <div className={s2025.name__cont}>
            <div className={s2025.name__name}>{name}</div>
            <div className={s2025.name__category}>{category}</div>
            {description &&
                <div className={s2025.name__description}>Описание: <span style={{color: '#000'}}>{description}</span>
                </div>}
            {brand &&
                <div className={s2025.name__brand}>Производитель: <span style={{color: '#000'}}>{brand}</span></div>}
            <div className={s2025.name__date}>Годен до: {date_avalible}</div>
        </div>
    )
}
const CategoryCascader = ({
                              categories,
                              onChange,
                              initialValue = ['1', '1']
                          }) => {
    const [value, setValue] = useState(initialValue);

    // Преобразуем данные в формат для Cascader
    const categoryOptions = useMemo(() => {
        return categories.mainCategory
            .sort((a, b) => parseInt(a.order_sort) - parseInt(b.order_sort))
            .map(category => {
                const subCategories = categories.subCategory
                    .filter(sub => sub.parent_id === category.id)
                    .map(sub => ({
                        value: sub.id,
                        label: sub.name
                    }));

                return {
                    value: category.id,
                    label: category.name,
                    children: subCategories.length > 0 ? subCategories : undefined
                };
            });
    }, [categories]);

    const handleChange = (newValue) => {
        setValue(newValue);
        onChange?.(newValue);
    };

    return (
        <Cascader
            options={categoryOptions}
            onChange={handleChange}
            value={value}
            placeholder="Выберите категорию и подкатегорию"
            style={{width: '100%'}}
            showSearch={{
                filter: (inputValue, path) => {
                    return path.some(option =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                    );
                }
            }}
            dropdownRender={(menu) => (
                <div style={{
                    width: '100%',
                }}>
                    {menu}
                </div>
            )}
        />

    );
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    componentDidCatch(error, errorInfo) {
        if (error.toString().includes('ResizeObserver')) {
            // Игнорировать ResizeObserver ошибки
            return;
        }
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || <div>Something went wrong.</div>;
        }
        return this.props.children;
    }
}

const Position = ({element, stores, change, username, onUpdate, onDelete}) => {


    if (!element) return null;

    return (
        <div>
            <StoreProgressBars
                item={element}
                stores={stores}
                onUpdate={onUpdate}
                username={username}
                onDelete={onDelete}
                showAll={false}
            />
            <HistoryTable
                itemId={element.id}
                itemName={element.name}
                change={change}
            />


        </div>
    );
};

export const NewStore = ({username}) => {



    const [store, setStore] = useState(null);
    const [stores, setStores] = useState(null);
    const [category, setCategory] = useState(null);
    const [load, setLoad] = useState(false);
    const [activeSubCategory, setActiveSubCategory] = useState(1);
    const [searchTerm, setSearchTerm] = useState(''); // Состояние для поиска
    const [sortConfig, setSortConfig] = useState({key: null, direction: 'asc'}); // Состояние для сортировки
    const [hideZeroItems, setHideZeroItems] = useState(false); // Новое состояние
    const [isFiltering, setIsFiltering] = useState(false);
    const [isPositioning, setIsPositioning] = useState(null);
    const [addItem, setAddItem] = useState(false);
    const [api, contextHolder] = notification.useNotification();
    const [transferModalVisible, setTransferModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [change, setChange] = useState(0);
    const [storeTypeFilter, setStoreTypeFilter] = useState(1); // 1, 2, 3 или null для всех
    const [specificStoreFilter, setSpecificStoreFilter] = useState(null); // конкретный склад
    const [availableStores, setAvailableStores] = useState([]);



    const hasStock = (item) => {
        // ИЗМЕНЕНИЕ: учитываем выбранный фильтр склада
        if (specificStoreFilter) {
            // Если выбран конкретный склад, проверяем наличие только на нем
            const specificStore = availableStores.find(store => store.id === specificStoreFilter);
            if (specificStore) {
                const storeValue = item[`store_${specificStore.name_db}`] || 0;
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;
                return numericValue > 0;
            }
            return false;
        } else if (storeTypeFilter !== null) {
            // Если выбран тип склада, проверяем наличие на складах этого типа
            const typedStores = getStoresByType(storeTypeFilter);
            return typedStores.some(store => {
                const storeValue = item[`store_${store.name_db}`] || 0;
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;
                return numericValue > 0;
            });
        } else {
            // Если нет фильтра, используем старую логику (все склады кроме типа 4)
            return sumStoreValues(item) > 0;
        }
    };

    const debouncedFilter = useCallback(
        debounce((value) => {
            setSearchTerm(value);
            setIsFiltering(false);
        }, 100),
        []
    );

    useEffect(() => {
        const source = axios.CancelToken.source();
        let isMounted = true;

        const fetchData = async () => {
            setLoad(true);
            try {
                const store_req = await instance.get(`store/get_store.php?subcategory_id=${activeSubCategory}`, {
                    cancelToken: source.token
                });

                if (isMounted && store_req.data) {
                    setStore(store_req.data.store);
                    setCategory(store_req.data.category);
                    setStores(store_req.data.stores);
                }
            } catch (e) {
                if (isMounted && !axios.isCancel(e)) {
                    console.error("Error fetching staff data:", e);
                }
            } finally {
                if (isMounted) {
                    setLoad(false);
                }
            }
        };

        fetchData();
        if (isPositioning){
            const element = stores.find(store => store.id === isPositioning.id)
            setIsPositioning(element);
        }

        // Cleanup функция
        return () => {
            isMounted = false;
            source.cancel('Component unmounted');
        };
    }, [activeSubCategory, change]);

    useEffect(() => {
        return () => {
            debouncedFilter.cancel(); // Очищаем debounce
        };
    }, [debouncedFilter]);

    // Обновляем доступные склады при изменении store или фильтров
    useEffect(() => {
        if (!store || !stores) return;

        // Получаем уникальные ID складов, где есть ненулевые позиции
        const storesWithItems = new Set();

        store.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key.startsWith('store_')) {
                    const storeValue = item[key];
                    const numericValue = typeof storeValue === 'string' ?
                        parseFloat(storeValue) || 0 :
                        Number(storeValue) || 0;

                    if (numericValue > 0) {
                        const storeId = key.replace('store_', '');
                        storesWithItems.add(storeId);
                    }
                }
            });
        });

        // Фильтруем склады: только активные
        // ИЗМЕНЕНИЕ: убираем проверку на наличие товаров, чтобы все активные склады были доступны для фильтрации
        const filteredStores = stores.filter(store =>
            store.enable === "1"
        );

        setAvailableStores(filteredStores);
    }, [store, stores]);



    // Функция для обработки сортировки
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({key, direction});
    };

    const handleHideZeroItems = () => {
        setIsFiltering(true);
        setHideZeroItems(!hideZeroItems);
        // Завершаем фильтрацию после небольшой задержки
        setTimeout(() => setIsFiltering(false), 2000);
    };

    // Функция для фильтрации данных по поисковому запросу
    const filterData = (data) => {
        let filtered = data;

        // Фильтрация по поисковому запросу
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (category && findSubCategoryName(Number(item.sub_category)).toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Фильтрация нулевых позиций - используем hasStock (теперь с учетом фильтров)
        if (hideZeroItems) {
            filtered = filtered.filter(item => hasStock(item));
        }

        return filtered;
    };

    const getStoresByType = (type) => {
        return stores.filter(store => {
            // ИЗМЕНЕНИЕ: используем исходный массив stores вместо availableStores
            if (type === null) return true;
            return Number(store.type) === type && store.enable === "1"; // Добавляем проверку на активность
        });
    };

    // Функция для получения складов типа 4 (списанные)
    const getWrittenOffStores = () => {
        return getStoresByType(4);
    };

    // Функция для получения детальной информации по списанным позициям по складам
    const getWrittenOffDetails = (item) => {
        const writtenOffStores = getWrittenOffStores();
        const details = [];

        writtenOffStores.forEach(store => {
            const storeValue = item[`store_${store.name_db}`] || 0;
            const numericValue = typeof storeValue === 'string' ?
                parseFloat(storeValue) || 0 :
                Number(storeValue) || 0;

            if (numericValue > 0) {
                details.push({
                    storeName: store.name,
                    value: numericValue
                });
            }
        });

        return details;
    };

    // Функция для подсчета суммы значений складов, исключая склады типа 4
    const sumStoreValues = (obj) => {
        let total = 0;

        for (const key in obj) {
            if (key.startsWith('store_')) {
                const storeValue = obj[key];
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;

                // Извлекаем номер склада из ключа
                const storeNumber = key.replace('store_', '');

                // Ищем склад в массиве stores по name_db
                const store = stores?.find(s => s.name_db === storeNumber);

                // Если склад найден, он активен И НЕ является складом типа 4, добавляем количество
                if (store && store.enable === "1" && Number(store.type) !== 4) {
                    total += numericValue;
                }
            }
        }
        return total;
    };

    // Функция для подсчета списанных позиций (склады типа 4)
    const sumWrittenOffValues = (obj) => {
        let total = 0;

        for (const key in obj) {
            if (key.startsWith('store_')) {
                const storeValue = obj[key];
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;

                // Извлекаем номер склада из ключа
                const storeNumber = key.replace('store_', '');

                // Ищем склад в массиве stores по name_db
                const store = stores?.find(s => s.name_db === storeNumber);

                // Если склад найден, он активен И является складом типа 4, добавляем количество
                if (store && store.enable === "1" && Number(store.type) === 4) {
                    total += numericValue;
                }
            }
        }
        return total;
    };

    const calculateOtherStoresValue = (item, excludedStores) => {
        let otherValue = 0;

        Object.keys(item).forEach(key => {
            if (key.startsWith('store_')) {
                const storeValue = item[key] || 0;
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;

                if (numericValue > 0) {
                    const storeNumber = key.replace('store_', '');

                    // Ищем склад в массиве stores
                    const store = stores?.find(s => s.name_db === storeNumber);

                    // Проверяем условия:
                    // 1. Склад активен (enable === "1")
                    // 2. Склад НЕ типа 4 (списанные)
                    // 3. Склад не входит в исключенные
                    if (store && store.enable === "1" && Number(store.type) !== 4) {
                        const isExcluded = excludedStores.some(excludedStore =>
                            excludedStore.name_db === storeNumber || excludedStore.key === key
                        );

                        if (!isExcluded) {
                            otherValue += numericValue;
                        }
                    }
                }
            }
        });

        return otherValue;
    };

    // Функция для сортировки данных
    const sortData = (data) => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            if (sortConfig.key === 'index') {
                return sortConfig.direction === 'asc' ? a.index - b.index : b.index - a.index;
            } else if (sortConfig.key === 'name') {
                return sortConfig.direction === 'asc'
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name);
            }
            return 0;
        });
    };
    if (!store || !category) return null

    const packageCorrector = (count, name) => {
        let correctorCount = count ? Number(count) : 0;

        if (correctorCount < 1) {
            switch (name) {
                case 'L':
                    return `${Math.round((correctorCount * 1000) * 1000) / 1000} ml`;
                case 'Kg':
                    return `${Math.round((correctorCount * 1000) * 1000) / 1000} g`;
                case 'M':
                    return `${Math.round((correctorCount * 100) * 1000) / 1000} sm`;
                default:
                    return ''
            }
        } else if (correctorCount >= 100 && name === 'sm') {
            return `${Math.round((correctorCount / 100) * 1000) / 1000} M`
        } else if (correctorCount >= 1000 && name !== 'шт' && name !== 'M2') {
            switch (name) {
                case 'ml':
                    return `${Math.round((correctorCount / 1000) * 1000) / 1000} L`;
                case 'g':
                    return `${Math.round((correctorCount / 1000) * 1000) / 1000} Kg`;
                case 'sm':
                    return `${Math.round((correctorCount / 100) * 1000) / 1000} M`;
                case 'M':
                    return `${Math.round((correctorCount / 1000) * 1000) / 1000} km`;
                default:
                    return ''
            }
        } else {
            return `${Math.round(correctorCount * 1000) / 1000} ${name}`
        }
    };


    const storeCorrector = (count, el) => {
        let correctorCount = Number(count);

        // ИЗМЕНЕНИЕ: учитываем фильтры при проверке на 0
        const hasStockWithFilters = hasStock(el);
        if (!hasStockWithFilters) {
            return 0;
        }

        let selectedStores = [];
        let otherStoresValue = 0;
        let writtenOffValue = sumWrittenOffValues(el); // Количество списанных позиций

        if (specificStoreFilter) {
            // Показываем только конкретный выбранный склад
            const specificStore = availableStores.find(store => store.id === specificStoreFilter);
            if (specificStore && Number(specificStore.type) !== 4) {
                const storeValue = el[`store_${specificStore.name_db}`] || 0;
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;

                selectedStores = [{
                    key: `store_${specificStore.name_db}`,
                    name: specificStore.name,
                    color: getStoreColor(specificStore.id, specificStore.type),
                    type: specificStore.type,
                    value: numericValue
                }];
            }
        } else if (storeTypeFilter !== null) {
            // Показываем склады выбранного типа (исключая тип 4)
            if (Number(storeTypeFilter) !== 4) {
                const typedStores = getStoresByType(storeTypeFilter);
                selectedStores = typedStores.map(store => {
                    const storeValue = el[`store_${store.name_db}`] || 0;
                    const numericValue = typeof storeValue === 'string' ?
                        parseFloat(storeValue) || 0 :
                        Number(storeValue) || 0;

                    return {
                        key: `store_${store.name_db}`,
                        name: store.name,
                        color: getStoreColor(store.id, store.type),
                        type: store.type,
                        value: numericValue
                    };
                });

                // Вычисляем сумму по другим складам (не выбранного типа и не типа 4)
                otherStoresValue = calculateOtherStoresValue(el, typedStores);
            }
        } else {
            // Показываем три основных склада по умолчанию + другие (исключая тип 4)
            const defaultStoresConfig = [
                {key: 'store_1', name: 'Будва', type: '1', name_db: '1'},
                {key: 'store_2', name: 'Лазуре', type: '1', name_db: '2'},
                {key: 'store_13', name: 'Сплендит', type: '1', name_db: '13'}
            ];

            selectedStores = defaultStoresConfig.map(store => {
                const storeValue = el[store.key] || 0;
                const numericValue = typeof storeValue === 'string' ?
                    parseFloat(storeValue) || 0 :
                    Number(storeValue) || 0;

                return {
                    ...store,
                    color: getStoreColor(store.key, store.type),
                    value: numericValue
                };
            });

            // Вычисляем сумму по другим складам (не входящим в основные и не типа 4)
            otherStoresValue = calculateOtherStoresValue(el, defaultStoresConfig);
        }

        // Фильтруем склады с нулевым количеством (кроме случая, когда выбран конкретный склад)
        if (!specificStoreFilter) {
            selectedStores = selectedStores.filter(store => store.value > 0);
        }

        // Добавляем "Другие" в список, если есть значение (только для НЕ типа 4)
        if (otherStoresValue > 0) {
            selectedStores.push({
                key: 'other_stores',
                name: 'Другие',
                color: '#d9d9d9',
                type: 'other',
                value: otherStoresValue
            });
        }

        // Добавляем "Списано" в список, если есть значение
        if (writtenOffValue > 0) {
            selectedStores.push({
                key: 'written_off',
                name: 'Списано',
                color: '#f5222d',
                type: '4',
                value: writtenOffValue,
                details: getWrittenOffDetails(el)
            });
        }

        // Находим максимальное значение для нормализации прогресса
        const maxStoreValue = Math.max(...selectedStores.map(store => store.value));

        return (
            <div className={s2025.countPack}>
                {selectedStores.map(store => {
                    const percent = maxStoreValue > 0 ? (store.value / maxStoreValue) * 100 : 0;

                    return (
                        <div key={store.key}
                             style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: '5px'}}>
                            <Tag color={store.color} style={{margin: 0, minWidth: 200}}>
                                {store.name}
                            </Tag>
                            <Progress
                                percent={percent}
                                showInfo={false}
                                size="small"
                                strokeColor={store.color}
                            />
                            <span style={{fontWeight: 'bold', minWidth: 50}}>
                                {store.value.toFixed(2)}
                            </span>
                            {/* Подсказка с деталями для списанных позиций */}
                            {store.type === '4' && store.details && store.details.length > 0 && (
                                <Tooltip
                                    title={
                                        <div>
                                            {store.details.map((detail, index) => (
                                                <div key={index}>
                                                    {detail.storeName}: {detail.value.toFixed(2)}
                                                </div>
                                            ))}
                                        </div>
                                    }
                                >
                                    <InfoCircleOutlined style={{color: '#1890ff', cursor: 'pointer'}} />
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const allstorecount = (count, el) => {
        let correctorCount = Number(count);
        // Добавляем отображение списанных позиций
        const writtenOffCount = sumWrittenOffValues(el);

        return (
            <div className={s2025.countPack}>
                <div>Единиц: <span style={{fontWeight: 'bold'}}>{correctorCount.toFixed(2)}</span></div>
                <div>Вес (объем): <span
                    style={{fontWeight: 'bold'}}>{packageCorrector(correctorCount * Number(el.package), el.package_name)}</span>
                </div>
                {writtenOffCount > 0 && (
                    <div style={{color: '#f5222d', fontSize: '12px'}}>
                        Списано: {writtenOffCount.toFixed(2)}
                    </div>
                )}
            </div>
        );
    }


// Функция для получения цвета склада с вариациями внутри типа
    const getStoreColor = (storeId, type) => {
        const baseColors = {
            '1': ['#1890ff', '#096dd9', '#0050b3'], // Основные - оттенки синего
            '2': ['#fa8c16', '#d46b08', '#ad4e00'], // Авто - оттенки оранжевого
            '3': ['#52c41a', '#389e0d', '#237804']  // Сотрудники - оттенки зеленого
        };

        // Создаем хэш из ID склада для детерминированного выбора цвета
        const hash = storeId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const colorIndex = hash % baseColors[type].length;

        return baseColors[type][colorIndex];
    };

    const findSubCategoryName = (id) => {
        let name = '';
        for (let i = 0, len = category.subCategory.length; i < len; i++) {
            if (Number(category.subCategory[i].id) === id) {
                name = category.subCategory[i].name
            }
        }
        return name
    };


    const openTransferModal = (item) => {
        setSelectedItem(item);
        setTransferModalVisible(true);
    };

    console.log(isPositioning);

// Функция обработки успешного перемещения
    const handleTransferSuccess = (result) => {
        // Обновляем данные после перемещения
        // Можно добавить логику для обновления состояния
        setChange(change + 1)
        api.open({
            message: 'Позиция успешно перемещена',
            description: `Перемещено ${result.quantity} единиц`,
            placement: 'topRight',
            duration: 4,
        });
    };


    const filteredStore = store ? store.map((el) => {
        return {
            ...el,
            key: el.id,
            allcount: allstorecount(sumStoreValues(el), el)
        };

    }).filter((el) => {
        return el !== '';
    }) : [];


    const processedData = sortData(filterData(filteredStore));

    let columns = [
        {
            title: (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <span>Арт</span>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => handleSort('index')}
                        icon={
                            sortConfig.key === 'index' ?
                                (sortConfig.direction === 'asc' ? <CaretUpOutlined/> : <CaretDownOutlined/>) :
                                <SortAscendingOutlined/>
                        }
                    />
                </div>
            ),
            dataIndex: 'id',
            key: 'index',
            width: '70px',
            fixed: true,
            align: 'center'
        },
        {
            title: (
                <div style={{display: 'flex', alignItems: 'center', minWidth: '300px'}}>
                    <span>Наименование</span>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => handleSort('name')}
                        icon={
                            sortConfig.key === 'name' ?
                                (sortConfig.direction === 'asc' ? <CaretUpOutlined/> : <CaretDownOutlined/>) :
                                <SortAscendingOutlined/>
                        }
                    />
                </div>
            ),
            dataIndex: 'name', // Убедитесь что это поле существует
            key: 'name',
            fixed: true,
            ellipsis: false,
            render: (text, record) => (
                <div onClick={() => setIsPositioning(record)}>
                    <ItemName
                        name={text}
                        category={findSubCategoryName(Number(record.sub_category))}
                        brand={record.brand}
                        description={record.description}
                        date_avalible={getDate(record.date_avalible)}
                        change={change}
                    />
                </div>
            )
        },
        {
            title: 'Ед.изм.',
            dataIndex: 'package',
            key: 'package',
            width: '70px',
            fixed: true,
            align: 'center',
            render: (text, record) => <div>{`${text}${record.package_name}`}</div>
        },
        {
            title: 'Общее количество',
            dataIndex: 'allcount',
            key: 'allcount',
            width: '150px',
            fixed: true,
            align: 'center',
            render: (text, record) => <div>{allstorecount(sumStoreValues(record), record)}</div>
        },
        {
            title: 'Количество по складам',
            dataIndex: 'count',
            width: 500,
            render: (text, record) => <div>{storeCorrector(sumStoreValues(record), record)}</div>
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 80,
            fixed: true,
            align: 'center',
            render: (_, record) => (
                <Button
                    icon={<DoubleRightOutlined/>}
                    onClick={() => openTransferModal(record, 'store_1')} // '1' - ID склада Будва
                    title="Переместить"
                    size="small"
                />
            )
        }
    ];

    const onChange = (name) => {
        console.log(name);
    };

    const onCategoryChange = (ids) => {
        console.log(ids);
        setActiveSubCategory(ids ? ids[1] : null);
    };

    const handleAddProduct = (newId) => {
        setChange(change + 1)
        api.open({
            type: 'success',
            message: 'Позиция успешно добавлена',
            description: `Позиция была успешно добавлена на склад.`,
            placement: 'topRight',
            duration: 4,
        });
    };

    const handleDeleteProduct = (newId) => {
        setChange(change + 1)
        setIsPositioning(null)
    };


    const handleSearch = (e) => {
        const value = e.target.value;
        setIsFiltering(true); // Начинаем фильтрацию
        debouncedFilter(value);
    };

    return (
        <div>
            {contextHolder}
            <div className={'store_container'}>
                <div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '0' }}>
                        {/* Категории */}
                        <div style={{ minWidth: '100%', flex: '1', padding: '20px 20px 10px' }}>
                            <CategoryCascader
                                categories={category}
                                onChange={onCategoryChange}
                                style={{ width: '100%' }}
                                placeholder="Выберите категорию"
                            />
                        </div>

                        {/* Поиск */}
                        <div style={{minWidth: '100%', flex: '1', padding: '0 20px 10px' }}>
                            <Input
                                placeholder="Поиск по названию и категории..."
                                value={searchTerm}
                                onChange={handleSearch}
                                allowClear
                                prefix={<SearchOutlined />}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    {/* Фильтры и действия */}
                    <div className={s2025.search_inpt__cont}>
                        {/* Фильтр по типу склада */}
                        <div className={s2025.search_inpt__item}>
                            <Select
                                size="middle"
                                style={{ width: 140 }}
                                placeholder="Тип склада"
                                value={storeTypeFilter}
                                onChange={(value) => {
                                    setStoreTypeFilter(value);
                                    setSpecificStoreFilter(null);
                                }}
                                allowClear
                            >
                                <Select.Option value={1}>Основные</Select.Option>
                                <Select.Option value={2}>Авто</Select.Option>
                                <Select.Option value={3}>Сотрудники</Select.Option>
                                <Select.Option value={4}>Списано</Select.Option>
                            </Select>
                        </div>

                        {/* Фильтр по конкретному складу */}
                        <div className={s2025.search_inpt__item}>
                            <Select
                                size="middle"
                                style={{ width: 160 }}
                                placeholder="Конкретный склад"
                                value={specificStoreFilter}
                                onChange={setSpecificStoreFilter}
                                allowClear
                                disabled={storeTypeFilter === null}
                            >
                                {storeTypeFilter !== null &&
                                    getStoresByType(storeTypeFilter).map(store => (
                                        <Select.Option key={store.id} value={store.id}>
                                            {store.name}
                                        </Select.Option>
                                    ))
                                }
                            </Select>
                        </div>

                        {/* Кнопка сброса фильтров склада */}
                        {(storeTypeFilter !== null || specificStoreFilter !== null) && (
                            <div className={s2025.search_inpt__item}>
                                <Button
                                    size="middle"
                                    icon={<ReloadOutlined />}
                                    onClick={() => {
                                        setStoreTypeFilter(null);
                                        setSpecificStoreFilter(null);
                                    }}
                                >
                                    Сбросить фильтр
                                </Button>
                            </div>
                        )}

                        {/* Кнопка сброса поиска и сортировки */}
                        <div className={s2025.search_inpt__item}>
                            <Button
                                size="middle"
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    setSearchTerm('');
                                    setSortConfig({ key: null, direction: 'asc' });
                                }}
                            >
                                Сбросить всё
                            </Button>
                        </div>

                        {/* Кнопка скрыть/показать отсутствующие */}
                        <div className={s2025.search_inpt__item}>
                            <Button
                                size="middle"
                                type={hideZeroItems ? "primary" : "default"}
                                onClick={handleHideZeroItems}
                                icon={hideZeroItems ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                loading={isFiltering}
                            >
                                {hideZeroItems ? 'Показать все' : 'Скрыть отсутствующие'}
                            </Button>
                        </div>

                        {/* Кнопка добавить */}
                        <div className={s2025.search_inpt__item}>
                            <Button
                                size="middle"
                                type="primary"
                                onClick={() => setAddItem(true)}
                                icon={<PlusSquareOutlined />}
                            >
                                Добавить
                            </Button>
                        </div>

                        {/* Кнопка экспорта в Excel */}
                        <div className={s2025.search_inpt__item}>
                            <ExcelExportButton items={store} stores={stores} />
                        </div>
                    </div>
                </div>

                <ErrorBoundary>
                    <Table
                        loading={load || isFiltering}
                        size={'small'}
                        bordered={true}
                        pagination={false}
                        columns={columns}
                        dataSource={processedData}
                        onChange={onChange}
                        rowClassName={(record, index) => (index % 2 !== 0 ? "red" : "green")}
                    />
                </ErrorBoundary>
            </div>
            <Modal
                title={isPositioning ? isPositioning.name : ''}
                closable={{'aria-label': 'Custom Close Button'}}
                open={isPositioning}
                okText={'Закрыть'}
                cancelButtonProps={{style: {display: 'none'}}}
                onOk={() => setIsPositioning(null)}
                onCancel={() => setIsPositioning(null)}
                width={950}
            >
                <Position onDelete={handleDeleteProduct} element={isPositioning} stores={stores} change={change}
                          username={username} onUpdate={() => setChange(change + 1)}/>
            </Modal>
            <AddProductModal
                visible={addItem}
                onAdd={handleAddProduct}
                onClose={() => setAddItem(false)}
                categories={category}
            />
            <TransferModal
                visible={transferModalVisible}
                onClose={() => setTransferModalVisible(false)}
                item={selectedItem}
                stores={stores}
                username={username}
                onTransferSuccess={handleTransferSuccess}
            />
        </div>
    );
};


/*
const Store = (props) => {


    let [changeElem, setChangeElem] = useState(false);
    let [currentElem, setCurrentElem] = useState({})

    let [transferElem, setTransferElem] = useState(false);
    let [empty, setEmpty] = useState(0);

    const handleChange = (pagination, filters, sorter) => {
        console.log('Various parameters', pagination, filters, sorter);

    };
    const tabListNoTitle = props.category.mainCategory.map((el) => ({key: Number(el.id), tab: el.name}))
    const eventListenerTransfer = (e) => {
        setTransferElem(!transferElem)
        setCurrentElem(e)
    };
    const eventListenerChange = (e) => {
        setChangeElem(!changeElem)
        setCurrentElem(e)
    };
    let columns = [
        {
            title: 'Арт',
            dataIndex: 'index',
            key: 'index',
            width: '50px',
            fixed: true,
            align: 'center'

        },
        {
            title: 'Наименование',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => (a.name > b.name) ? 1 : -1,
            sortDirections: ['ascend'],
            width: 250,
            fixed: true,
            ellipsis: false,
            filters: '',
            onFilter: (value, record) => record.subCategoryName.includes(value),
            render: (text, record) => <ItemName name={text} category={record.subCategoryName} brand={record.brand} description={record.description} />
        },
        {
            title: 'Ед.изм.',
            dataIndex: 'package',
            key: 'package',
            width: '70px',
            fixed: true,
            align: 'center'
        },
        {
            title: 'Действия',
            dataIndex: 'action',
            key: 'action',
            width: '100px',
            fixed: true,
            align: 'center',
            render: (action) => {
                return <><Button className={'btnTransfer'} onClick={() => {
                    eventListenerTransfer(action)
                }} size={"small"} type="primary"><DoubleRightOutlined placeholder={'Переместить'}/></Button><Button
                    onClick={() => {
                        eventListenerChange(action)
                    }} className={'changeIco'} size={"small"} type="primary"><EditOutlined
                    placeholder={'Изменить'}/></Button></>
            },

        }


        ,
/!*        {
            title: 'Категория',
            dataIndex: 'subCategoryName',
            key: 'subCategoryName',
            width: '200px',
            filters: '',
            onFilter: (value, record) => record.subCategoryName.includes(value),

        },*!/
        {
            title: 'Количество',
            children: '',
        },
        {
            title: 'Годен до',
            dataIndex: 'date_avalible',
            key: 'date_avalible',
            width: '80px',
            align: 'center',

        },
/!*        {
            title: 'Производитель | Примечание',
            dataIndex: 'brand',
            key: 'brand',
            width: '150px',
            align: 'center',
            render: (brand, obj) => <BrandComp brand={brand} obj={obj}/>,
        }*!/

    ];

    let stores = props.stores.filter( el => Number(el.enable) !== 0)



    const packageCorrector = (count, name) => {
        let correctorCount = count ? Number(count) : 0;

        if (correctorCount < 1) {
            switch (name) {
                case 'L':
                    return `${Math.round((correctorCount * 1000) * 1000) / 1000} ml`;
                case 'Kg':
                    return `${Math.round((correctorCount * 1000) * 1000) / 1000} g`;
                case 'M':
                    return `${Math.round((correctorCount * 100) * 1000) / 1000} sm`;
                default:
                    return ''
            }
        } else if (correctorCount >= 100 && name === 'sm') {
            return `${Math.round((correctorCount / 100) * 1000) / 1000} M`
        } else if (correctorCount >= 1000 && name !== 'шт' && name !== 'M2') {
            switch (name) {
                case 'ml':
                    return `${Math.round((correctorCount / 1000) * 1000) / 1000} L`;
                case 'g':
                    return `${Math.round((correctorCount / 1000) * 1000) / 1000} Kg`;
                case 'sm':
                    return `${Math.round((correctorCount / 100) * 1000) / 1000} M`;
                case 'M':
                    return `${Math.round((correctorCount / 1000) * 1000) / 1000} km`;
                default:
                    return ''
            }
        } else {
            return `${Math.round(correctorCount * 1000) / 1000} ${name}`
        }
    };
    const storeCorrector = (count, el) => {

        let correctorCount = Number(count)
        return correctorCount === 0 ? 0
            :
            <div className={s.countPack}>
                <b>{`${packageCorrector(correctorCount * Number(el.package), el.package_name)}`}</b>{` | ${correctorCount}`}
            </div>

    }
    const findCategoryId = (id) => {
        let categoryId = null;
        for (let i = 0, len = props.category.subCategory.length; i < len; i++) {
            if (Number(props.category.subCategory[i].id) === id) {
                categoryId = props.category.subCategory[i].parent_id
            }
        }
        return Number(categoryId)
    };
    const findSubCategoryName = (id) => {
        let name = '';
        for (let i = 0, len = props.category.subCategory.length; i < len; i++) {
            if (Number(props.category.subCategory[i].id) === id) {
                name = props.category.subCategory[i].name
            }
        }
        return name
    };
    const findCategoryName = (id) => {
        let name = '';
        for (let i = 0, len = props.category.mainCategory.length; i < len; i++) {
            if (Number(props.category.mainCategory[i].id) === id) {
                name = props.category.mainCategory[i].name
            }
        }
        return name
    };
    const findModificator = (id) => {
        let modificator = null;
        for (let i = 0, len = props.category.mainCategory.length; i < len; i++) {
            if (Number(props.category.mainCategory[i].id) === id) {
                modificator = props.category.mainCategory[i].modificator
            }
        }
        return Number(modificator)
    };

    const arr = props.storeData.map((el) => {
        // Создание из серверных данных основного массива товаров, с необходимыми свойствами
        let categoryId = findCategoryId(Number(el.sub_category));
        let mod = findModificator(categoryId);
        let subCategoryName = findSubCategoryName(Number(el.sub_category));
        let mainCategoryName = findCategoryName(categoryId);
        let summ = 0;
        let s = {
            index: Number(el.id),
            key: Number(el.id),
            maincatId: categoryId,
            mainCategoryName: mainCategoryName,
            subCatId: Number(el.sub_category),
            subCategoryName: subCategoryName,
      /!*      brand: `${el.brand} | ${el.description}`,*!/
            brand: el.brand,
            name: mod === 0 ? el.name : `${el.brand !== '' ? el.brand + ' | ' + el.name : el.name}`,
            package: `${el.package}${el.package_name}`,
            description: el.description,
            action: {
                ...el,
                catname: subCategoryName,
                maincatId: categoryId,
                maincatName: mainCategoryName,
                subCatId: Number(el.sub_category),
                subCategoryName: subCategoryName
            },
            date_avalible: getDate(el.date_avalible)
        };
        for (let i = 0; i < stores.length; i++) {

            s[`store_${stores[i].name_db}`] = storeCorrector(el[`store_${stores[i].name_db}`], el)
        }
        for (let i = 0; i < stores.length; i++) {
            if (Number(stores[i].type) === Number(props.storesTypeId)) {
                summ += Number(el[`store_${stores[i].name_db}`])
            }

        }

        if (empty === 0) {
            return s
        } else if (empty === 1 && Number(summ) === 0) {
            return ''
        } else if (empty === 1 && Number(summ) !== 0) {
            return s
        } else if (empty === 2 && Number(summ) === 0) {
            return s
        } else if (empty === 2 && Number(summ) !== 0) {
            return ''
        } else {
            return s
        }
    }).filter((el) => {
        return el !== ''
    });


    return (
        <div>
            <StoreCategoryTab
                tabListNoTitle={tabListNoTitle}
                columns={columns}
                Arr={arr}
                handleChange={handleChange}
                category={props.category}
                stores={stores}
                addModal={props.addModal}
                setAddModal={props.SETADDSTOREAC}
                post={props.post}
                storesTypeId={props.storesTypeId}
                setEmpty={setEmpty}
                empty={empty}

            />
            {transferElem ? <ModalWin update={props.transfer}
                                      element={currentElem}
                                      modal={transferElem}
                                      setModal={() => {
                                          setTransferElem()
                                      }}
                                      stores={stores}
                                      storesTypeId={props.storesTypeId}
            /> : ''}


            {changeElem ? <ChangeWin element={currentElem}
                                     change={changeElem}
                                     setModal={() => {
                                         setChangeElem()
                                     }}
                                     category={props.category}
                                     post={props.changeElem}


            /> : ''}


        </div>
    )


}


export default Store;*/
